// FHIR
import Client from "fhir-kit-client";
import { Bundle, Device } from "fhir/r5";

export default class DeviceService {
    constructor(private readonly client: Client) {}

    /**
     * Search devices used as software/application assets for the information system map.
     *
     * Gateway is required to build the graph. Some FHIR servers may not return
     * CodeableReference fields reliably when _elements is used, so the map loads
     * full Device resources in this first version.
     */
    async searchSoftwareDevices(): Promise<Device[]> {
        const firstPage = await this.client.search({
            resourceType: "Device",
            searchParams: {
                _sort: "-_lastUpdated",
                _count: "100",
            },
        }) as Bundle;

        return this.collectDevicesFromPaginatedBundle(firstPage);
    }

    private async collectDevicesFromPaginatedBundle(firstPage: Bundle): Promise<Device[]> {
        const devices: Device[] = [];
        let currentPage: Bundle | undefined = firstPage;
        let pageCount = 0;
        const maxPages = 50;

        while (currentPage && pageCount < maxPages) {
            devices.push(...this.extractDevices(currentPage));
            currentPage = await this.getNextPage(currentPage);
            pageCount += 1;
        }

        return devices;
    }

    private extractDevices(bundle: Bundle): Device[] {
        return bundle.entry?.map((entry) => entry.resource as Device).filter(Boolean) ?? [];
    }

    private async getNextPage(bundle: Bundle): Promise<Bundle | undefined> {
        const nextUrl = bundle.link?.find((link) => link.relation === "next")?.url;

        if (!nextUrl) {
            return undefined;
        }

        const paginatedClient = this.client as any;

        if (typeof paginatedClient.nextPage === "function") {
            try {
                return await paginatedClient.nextPage(bundle) as Bundle;
            } catch (error) {
                try {
                    return await paginatedClient.nextPage({ bundle }) as Bundle;
                } catch (fallbackError) {
                    console.log(fallbackError);
                }
            }
        }

        if (typeof paginatedClient.request === "function") {
            return await paginatedClient.request({
                url: nextUrl,
                method: "GET",
            }) as Bundle;
        }

        return undefined;
    }
}
